import { confirm, solveDecimalJavascriptSum } from 'utils'

import { ledgerMoney } from './ledgerSort'

/**
 * A rounding crumb is not an overdraft. Every balance here is struck by summing rows that carry
 * cents, so a withdrawal that empties the bucket to the penny must not be warned about.
 */
const TOLERANCE = 0.005

/**
 * Warn before a trust transaction that takes more money out of a ledger than it holds.
 *
 * The ledger is a record of what happened, not a gate on what may happen: a servicer does post a
 * disbursement before the money that covers it has landed, and an imported loan can open short.
 * So this asks rather than refuses. Resolves true when it is safe to carry on (enough funds, or
 * the user chose to continue anyway) and false when the user cancelled, in which case the caller
 * must not write the transaction.
 *
 * `available` is the balance the entry comes out of with the row being edited added back in, so
 * raising an existing $100 withdrawal to $120 is measured against the $20 it actually takes and
 * not against a balance that still has the original $100 removed from it.
 */
export const confirmSufficientFunds = async (
  withdrawal: number,
  available: number,
  ledgerName: string,
): Promise<boolean> => {
  if (!(withdrawal > 0)) return true
  const shortfall = solveDecimalJavascriptSum([withdrawal, -1 * available])
  if (shortfall <= TOLERANCE) return true

  // Led by the ledger rather than by the transaction, so the sentence reads the same whether the
  // name carries its own article ("Suspense", "Impounds Ledger", a trust account's name).
  let holding = `${ledgerName} has no funds available.`
  if (available > 0) holding = `${ledgerName} holds ${ledgerMoney(available)}.`
  else if (available < 0) holding = `${ledgerName} is already overdrawn by ${ledgerMoney(-1 * available)}.`

  // The confirm dialog renders its content as HTML, so the break has to be a tag.
  const answer = await confirm(
    `${holding} This transaction takes ${ledgerMoney(withdrawal)} out of it, ${ledgerMoney(shortfall)} more ` +
      `than is available.<br/><br/>Would you like to continue?`,
  )
  return !!answer
}
